import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  TextField,
  InputAdornment,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchIcon from "@mui/icons-material/Search";
import { alertActions } from "_store";
import EnergyAssistanceDetails from "./components/Dashboard/Details";
import { Controller, useForm } from "react-hook-form";
import {
  getAccountLookupApi,
  getAdvanceSearchApi,
  getTooltipMsgsApi,
} from "services/energyAssistance/energyAssistance.thunks";
import { getTooltipMsgs } from "selectors/energyAssistance.selectors";
import { ERR_MSG } from "constants/labels";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import AdvanceFilter from "./components/AdvanceFilter";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { FilterListOff } from "@mui/icons-material";
import { InfoTooltip } from "_components";

export const EnergyAssistance = () => {
  const header = "Dashboard";
  const dispatch = useDispatch();
  const [isAccountValid, setIsAccountValid] = useState(false);
  const [openRowId, setOpenRowId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [accountNumber, setAccountNumber] = useState(null);
  const [searchMessage, setSearchMessage] = useState(null);
  const tooltipMsgs = useSelector(getTooltipMsgs);
  const getTooltip = (title) =>
    tooltipMsgs?.Data?.find((t) => t.TooltipTitle === title)?.TooltipMsg;

  useEffect(() => {
    dispatch(getTooltipMsgsApi());
  }, []);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    defaultValues,
  } = useForm({
    defaultValues: {
      accountNumber: "",
    },
  });

  const handleRowExpandToggle = (row) => {
    if (openRowId === row.id) {
      setOpenRowId(null);
    } else {
      setOpenRowId(row.id);
      row.toggleExpanded(row.id);
      setAccountNumber(row.id);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "AccountNumber",
        header: "Account #",
        Header: () => (
          <>
            Account #
            <InfoTooltip
              color="inherit"
              title="Account Number"
              message={getTooltip("Account Number")}
            />
          </>
        ),
        size: 130,
        Cell: ({ row }) => (
          <Typography
            onClick={() => handleRowExpandToggle(row)}
            sx={{
              color: "#1976d2",
              cursor: "pointer",
              fontWeight: "500",
              "&:hover": {
                color: "#115293",
              },
              paddingY: "16px",
              textAlign: "center",
            }}
          >
            {row.original.AccountNumber}
          </Typography>
        ),
      },
      {
        accessorKey: "AccountStatus",
        header: "Account Status",
        Header: () => (
          <>
            Account Status
            <InfoTooltip
              color="inherit"
              title="Account Status"
              message={getTooltip("Account Status")}
            />
          </>
        ),
        size: 130,
      },
      {
        accessorKey: "CustomerName",
        header: "Customer Name",
        Header: () => (
          <>
            Customer Name
            <InfoTooltip
              color="inherit"
              title="Customer Name"
              message={getTooltip("Customer Name")}
            />
          </>
        ),
        size: 170,
      },
      {
        accessorKey: "ServiceAddress",
        header: "Service Address",
        Header: () => (
          <>
            Service Address
            <InfoTooltip
              color="inherit"
              title="Service Address"
              message={getTooltip("Service Address")}
            />
          </>
        ),
        size: 200,
      },
      {
        accessorKey: "ServiceOn",
        header: "Service Address Gas Status",
        Header: () => (
          <>
            Service Address Gas Status
            <InfoTooltip
              color="inherit"
              title="Service Address Gas Status"
              message={getTooltip("Service Address Gas Status")}
            />
          </>
        ),
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {value ? (
                <>
                  <Typography component="div">
                    <CheckCircleOutlineIcon />
                  </Typography>
                  <Typography component="div" marginTop={"4px"}>
                    ON
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="div">
                    <CancelOutlinedIcon />
                  </Typography>
                  <Typography component="div" marginTop={"4px"}>
                    OFF
                  </Typography>
                </>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "HeatingAccount",
        header: "Heating Account",
        Header: () => (
          <>
            Heating Account
            <InfoTooltip
              color="inherit"
              title="Heating Account"
              message={getTooltip("Heating Account")}
            />
          </>
        ),
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {value ? (
                <Typography component="div" marginTop={"4px"}>
                  YES
                </Typography>
              ) : (
                <Typography component="div" marginTop={"4px"}>
                  NO
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "AmountDue",
        header: "Amount Due",
        Header: () => (
          <>
            Amount Due
            <InfoTooltip
              color="inherit"
              title="Amount Due"
              message={getTooltip("Amount Due")}
            />
          </>
        ),
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box sx={{ textAlign: "center" }}>
              {value !== null && value !== undefined
                ? `$${Number(value).toFixed(2)}`
                : "$0.00"}
            </Box>
          );
        },
      },
      {
        accessorKey: "OpenBalances",
        header: "Open Balance",
        Header: () => (
          <>
            Open Balance
            <InfoTooltip
              color="inherit"
              title="Open Balance"
              message={getTooltip("Open Balance")}
            />
          </>
        ),
        size: 130,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box sx={{ textAlign: "center" }}>
              {value !== null && value !== undefined
                ? `$${Number(value).toFixed(2)}`
                : "$0.00"}
            </Box>
          );
        },
      },
      {
        accessorKey: "SecurityDepositAmountAssessedInTotal",
        header: "Total Security Deposit Assessed",
        Header: () => (
          <>
            Total Security Deposit Assessed
            <InfoTooltip
              color="inherit"
              title="Total Security Deposit Assessed"
              message={getTooltip("Total Security Deposit Assessed")}
            />
          </>
        ),
        size: 180,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box sx={{ textAlign: "center" }}>
              {value !== null && value !== undefined
                ? `$${Number(value).toFixed(2)}`
                : "$0.00"}
            </Box>
          );
        },
      },
      {
        accessorKey: "SecurityDepositOpenAmount",
        header: "Open Security Deposit Amount",
        Header: () => (
          <>
            Open Security Deposit Amount
            <InfoTooltip
              color="inherit"
              title="Open Security Deposit Amount"
              message={getTooltip("Open Security Deposit Amount")}
            />
          </>
        ),
        size: 180,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box sx={{ textAlign: "center" }}>
              {value !== null && value !== undefined
                ? `$${Number(value).toFixed(2)}`
                : "$0.00"}
            </Box>
          );
        },
      },
    ],
    [handleRowExpandToggle, tooltipMsgs.Data]
  );

  const handleClearAllFilters = (table) => {
    table && table.resetColumnFilters();
    setTableData([]);
    setSearchMessage(null);
    setOpenRowId(null);
    setAccountNumber(null);
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableExpandAll: false,
    enableExpanding: true,
    enablePagination: true,
    enableColumnFilters: true,
    enableFullScreenToggle: false,
    enableTopToolbar: true,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableFilters: true,
    enableColumnFilterModes: false,
    columnFilterDisplayMode: "popover",
    enableHiding: false,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    getRowId: (row) => row.AccountNumber,
    state: {
      expanded: openRowId ? { [openRowId]: true } : {},
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        muiTableHeadCellProps: {
          sx: {
            display: "none",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: "none",
          },
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#0F3557",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
        "& .Mui-TableHeadCell-Content": {
          alignItems: "center !important",
          justifyContent: "center !important",
        },
        "& .Mui-TableHeadCell-Content-Labels": {
          display: "flex !important",
          flexDirection: "row !important",
          flexWrap: "wrap !important",
          alignItems: "center !important",
          justifyContent: "center !important",
          width: "100% !important",
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          flexBasis: "100% !important",
          whiteSpace: "normal !important",
          textAlign: "center !important",
        },
        "& .Mui-TableHeadCell-Content-Actions": {
          marginLeft: "0 !important",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: { textAlign: "center" },
    },
    muiTableContainerProps: {
      sx: {
        overflowX: "auto",
        width: "100%",
      },
    },
    initialState: {
      sorting: [{ id: "AccountNumber", desc: false }],
    },
    // NOTE: OpenBalances, SecurityDepositAmountAssessedInTotal, and
    // SecurityDepositOpenAmount come from the account-lookup API response
    // (row.original), not from getCurrentBillingApi. We pass them straight
    // through to EnergyAssistanceDetails -> CurrentBilling as props since
    // that data already lives in this table's row data.
    renderDetailPanel: ({ row }) =>
      openRowId === row.id && (
        <Box
          sx={{
            padding: 2,
          }}
        >
          <EnergyAssistanceDetails
            AccountNumber={accountNumber}
            OpenBalances={row.original.OpenBalances}
            SecurityDepositAmountAssessedInTotal={
              row.original.SecurityDepositAmountAssessedInTotal
            }
            SecurityDepositOpenAmount={
              row.original.SecurityDepositOpenAmount
            }
          />
        </Box>
      ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ p: 3, textAlign: "center", fontStyle: "italic" }}>
        No data available.
      </Typography>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            padding: "8px",
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Clear All Filters" className="Deactivate">
            <div>
              <IconButton
                onClick={() => handleClearAllFilters(table)}
              >
                <FilterListOff variant="contained" color="secondary" />
              </IconButton>
            </div>
          </Tooltip>
        </Box>
      );
    },
  });

  const onSearchSubmit = useCallback(async (data) => {
    setIsAccountValid(0);
    dispatch(alertActions.clear());
    try {
      const res = await dispatch(
        getAccountLookupApi(data.accountNumber)
      ).unwrap();
      const isExisting = res?.Data?.Exists || false;
      setIsAccountValid(isExisting);

      if (!res || !res?.Succeeded) {
        dispatch(
          alertActions.error({
            message: res?.Message || ERR_MSG["073"],
            header: "Energy Assistance",
          })
        );
        return;
      }

      if (!isExisting) {
        setIsAccountValid(0);
        dispatch(
          alertActions.error({
            header: "Energy Assistance",
            message: res?.Data?.Message,
          })
        );
      } else {
        const results = res?.Data?.Results || [];
        setTableData(results);
        setSearchMessage(res?.Data?.Message);

        if (results.length === 1) {
          setOpenRowId(results[0].AccountNumber);
          setAccountNumber(results[0].AccountNumber);
        } else {
          setOpenRowId(null);
          setAccountNumber(null);
        }
      }
    } catch (error) {
      setIsAccountValid(false);
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
        })
      );
    }
  }, []);

  const handleSearchChange = useCallback((e, field) => {
    const newValue = e.target.value;
    if (/^\d*$/.test(newValue) && newValue.length <= 12) {
      field.onChange(newValue);
    }
  }, []);

  return (
    <>
      <Typography component="div" className="userprofilelist ">
        <Grid container direction="row" spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography variant="h2" className="userprofilelistcontent">
              {header}
            </Typography>
          </Grid>
        </Grid>
      </Typography>
      <Typography
        component={"div"}
        display={"flex"}
        justifyContent={"flex-end"}
        width={"100%"}
      >
        <AdvanceFilter
          setFilterData={setTableData}
          apiCall={getAdvanceSearchApi}
          setOpenRowId={setOpenRowId}
          setAccountNumber={setAccountNumber}
          resetSearch={reset}
          defValueSearch={defaultValues}
          setSearchMessage={setSearchMessage}
        />
      </Typography>
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <Controller
          name="accountNumber"
          control={control}
          rules={{
            required: "Account Number is required",
            minLength: {
              value: 12,
            },
            maxLength: {
              value: 12,
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              className="SearchIconinput"
              margin="normal"
              placeholder="Search Account Number"
              onChange={(e) => {
                handleSearchChange(e, field);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      className="SearchIcon"
                      onClick={handleSubmit(onSearchSubmit)}
                      style={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
                "aria-label": "Search accounts",
              }}
            />
          )}
        />
      </form>

      <div className="MarketerList MapCentercontainer EnergyAssistance">
        {searchMessage && (
          <Typography
            component={"div"}
            sx={{
              border: "3px solid #71b2f3ff",
              padding: "8px",
              marginBottom: "16px",
              marginTop: "16px",
              borderRadius: "4px",
              fontWeight: "700",
              fontSize: "16px",
              color: "#0e80f1ff",
            }}
          >
            {searchMessage}
          </Typography>
        )}
        <MaterialReactTable table={table} />
      </div>
    </>
  );
};